import React, { useEffect, useMemo, useState } from 'react';
import useAdminFetchApi from '../../utils/adminFetchApi';
import { Link } from 'react-router-dom';
import { getImageUrl, handleImageError } from '../../utils/imageUtils';

export default function AdminProfiles() {
  const api = useAdminFetchApi();
  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState('');
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [limit, setLimit] = useState(100);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(null); // optional if API returns
  const [thumbMap, setThumbMap] = useState({}); // uuid/id -> image url
  const [profilesFetchToken, setProfilesFetchToken] = useState(0); // increments on list fetch
  const [dateDrafts, setDateDrafts] = useState({}); // inline editable drafts for date_of_birth keyed by row key
  const [priorityDrafts, setPriorityDrafts] = useState({}); // inline editable drafts for priority keyed by row key
  const [savingIds, setSavingIds] = useState(new Set()); // rows being saved inline
  const [hobbyDrafts, setHobbyDrafts] = useState({}); // inline editable drafts for hobby keyed by row key
  // Edit moved to dedicated page route

  useEffect(() => {
    let mounted = true;
    let fetching = false;
    const loadChannels = async () => {
      if (fetching) return;
      fetching = true;
      try {
        const data = await api.get('/admin/channels');
        if (!mounted) return;
        const list = Array.isArray(data) ? data : (data?.channels || []);
        setChannels(list);
      } catch (e) {
        // handled in api
      } finally {
        fetching = false;
      }
    };
    loadChannels();
    return () => { mounted = false; };
  }, [api]);

  const fetchProfiles = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (selectedChannel) params.set('channel', selectedChannel);
      params.set('limit', String(limit));
      params.set('offset', String(offset));
      const qs = params.toString();
      const data = await api.get(`/admin/profiles${qs ? ('?' + qs) : ''}`);
      // Normalize response
      let rows = [];
      let t = null;
      const payload = data && data.data ? data.data : data; // support {data:{...}}
      if (Array.isArray(payload)) {
        rows = payload;
      } else if (Array.isArray(payload?.profiles)) {
        rows = payload.profiles;
        t = payload.total ?? null;
      } else if (Array.isArray(payload?.users)) { // fallback shape similar to public list
        rows = payload.users;
        t = payload.total ?? null;
      } else if (Array.isArray(data?.profiles)) {
        rows = data.profiles;
        t = data.total ?? null;
      } else if (Array.isArray(data?.users)) {
        rows = data.users;
        t = data.total ?? null;
      } else if (Array.isArray(data?.items)) {
        rows = data.items;
        t = data.total ?? null;
      } else if (Array.isArray(data)) {
        rows = data;
      }
      setProfiles(Array.isArray(rows) ? rows : []);
      if (t !== null) setTotal(t);
  setProfilesFetchToken(prev => prev + 1);
    } catch (e) {
      setError(e.message || 'Failed to load profiles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChannel, limit, offset]);

  // Fetch thumbnails for rows missing image info (trigger only after full list fetch, not inline edits)
  useEffect(() => {
    let mounted = true;
    const idsToFetch = profiles
      .filter(p => {
        const hasAny = p?.images?.[0]?.thumb_path || p?.images?.[0]?.image_path || p?.thumb_path || p?.image_path || p?.image_url || p?.image;
        return !hasAny;
      })
      .map(p => p.uuid)
      .filter(Boolean)
      .filter(id => !thumbMap[id]);

    if (idsToFetch.length === 0) return () => {};

    const fetchOne = async (uuid) => {
      try {
        const data = await api.get(`/admin/profiles/${uuid}`);
        const payload = data?.data || data;
        const imgPath = payload?.images?.[0]?.thumb_path || payload?.images?.[0]?.image_path || payload?.thumb_path || payload?.image_path || '';
        if (!mounted) return;
        if (imgPath) {
          setThumbMap(prev => ({ ...prev, [uuid]: getImageUrl(imgPath) }));
        }
      } catch (_) {
        // ignore failures per-row
      }
    };

    const run = async () => {
      const pool = 4;
      let i = 0;
      const workers = new Array(pool).fill(0).map(async () => {
        while (i < idsToFetch.length) {
          const id = idsToFetch[i++];
          await fetchOne(id);
        }
      });
      await Promise.all(workers);
    };
    if (idsToFetch.length > 0) run();
    return () => { mounted = false; };
  }, [api, thumbMap, profilesFetchToken]);

  const canPrev = offset > 0;
  const canNext = useMemo(() => {
    if (total == null) return profiles.length === limit; // best-effort if total missing
    return offset + limit < total;
  }, [offset, limit, total, profiles.length]);

  const nextPage = () => setOffset(prev => prev + limit);
  const prevPage = () => setOffset(prev => Math.max(0, prev - limit));

  const onChannelChange = (e) => {
    setSelectedChannel(e.target.value);
    setOffset(0); // reset pagination on filter change
  };

  const PROFILE_TYPE_OPTIONS = [
    { value: '', label: '—' },
    { value: '1', label: 'Couple' },
    { value: '2', label: 'Father with childs' },
    { value: '3', label: 'Mother with childs' },
    { value: '4', label: 'Full family' },
    { value: '5', label: 'Friends' },
  ];
  const GENDER_OPTIONS = [
    { value: '', label: '—' },
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'other', label: 'Other' },
  ];

  const toGenderOption = (val) => {
    if (val == null || val === '') return '';
    const s = String(val).toLowerCase().trim();
    if (s === 'male' || s === 'm' || s === '1') return 'male';
    if (s === 'female' || s === 'f' || s === '2') return 'female';
    if (s === 'other' || s === 'o' || s === '3' || s === 'non-binary' || s === 'nonbinary' || s === 'nb') return 'other';
    return '';
  };

  const getRowKey = (row) => row?.uuid || row?.id;
  const isRowSaving = (row) => {
    const key = getRowKey(row);
    return key ? savingIds.has(key) : false;
  };

  const normalizeFieldValue = (field, value) => {
    if (field === 'profile_type') return value === '' ? null : Number(value);
    if (field === 'priority') {
      // Accept empty => null, else coerce to integer
      if (value === '' || value == null) return null;
      const n = parseInt(String(value), 10);
      return isNaN(n) ? null : n;
    }
    if (field === 'gender') return value === '' ? null : value;
    if (field === 'date_of_birth') return value === '' ? null : value; // expect YYYY-MM-DD
    if (field === 'hobby') {
      const v = (value ?? '').toString().trim();
      return v === '' ? null : v;
    }
    if (field === 'is_public' || field === 'is_enabled' || field === 'is_enable') return !!value;
    return value;
  };

  const inlineUpdate = async (row, field, value) => {
    const key = getRowKey(row);
    if (!key) return;
    const normalized = normalizeFieldValue(field, value);
    const prevVal = row?.[field];
    // optimistic update
    setProfiles(prev => prev.map(r => ((getRowKey(r) === key) ? { ...r, [field]: normalized } : r)));
    setSavingIds(prev => { const n = new Set(prev); n.add(key); return n; });
    try {
      const payload = {};
      if (row.uuid) payload.uuid = row.uuid; else if (row.id) payload.id = row.id;
      payload[field] = normalized;
      await api.put('/admin/profile', payload);
    } catch (e) {
      // revert on failure
      setProfiles(prev => prev.map(r => ((getRowKey(r) === key) ? { ...r, [field]: prevVal } : r)));
      setError(e.message || 'Update failed');
    } finally {
      setSavingIds(prev => { const n = new Set(prev); n.delete(key); return n; });
    }
  };

  const commitDateDraft = async (row) => {
    const key = getRowKey(row);
    const draft = dateDrafts[key];
    const trimmed = (draft ?? '').trim();
    // Accept only empty or YYYY-MM-DD with basic month/day ranges
    let isValid = false;
    if (trimmed === '') {
      isValid = true;
    } else if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      const [yyyy, mm, dd] = trimmed.split('-');
      const m = parseInt(mm, 10);
      const d = parseInt(dd, 10);
      isValid = m >= 1 && m <= 12 && d >= 1 && d <= 31;
    }
    const valueToSend = isValid ? trimmed : (String(row?.date_of_birth || '').slice(0,10));
    await inlineUpdate(row, 'date_of_birth', valueToSend);
    // Clear draft after commit
    setDateDrafts(prev => { const n = { ...prev }; delete n[key]; return n; });
  };

  const commitPriorityDraft = async (row) => {
    const key = getRowKey(row);
    const draft = priorityDrafts[key];
    const trimmed = (draft ?? '').toString().trim();
    let valueToSend = null;
    if (trimmed !== '') {
      const n = parseInt(trimmed, 10);
      valueToSend = isNaN(n) ? null : n;
    }
    await inlineUpdate(row, 'priority', valueToSend);
    setPriorityDrafts(prev => { const n = { ...prev }; delete n[key]; return n; });
  };

  const commitHobbyDraft = async (row) => {
    const key = getRowKey(row);
    const draft = hobbyDrafts[key];
    const trimmed = (draft ?? '').toString().trim();
    const valueToSend = trimmed; // empty becomes null via normalizeFieldValue
    await inlineUpdate(row, 'hobby', valueToSend);
    setHobbyDrafts(prev => { const n = { ...prev }; delete n[key]; return n; });
  };

  const maskDateInput = (raw) => {
    // Strip non-digits
    const digits = String(raw || '').replace(/\D/g, '').slice(0, 8); // YYYYMMDD max 8 digits
    const len = digits.length;
    if (len <= 4) return digits;
    if (len <= 6) return `${digits.slice(0,4)}-${digits.slice(4)}`;
    return `${digits.slice(0,4)}-${digits.slice(4,6)}-${digits.slice(6)}`;
  };

  const getRowImageUrl = (row) => {
    const imgPath = row?.images?.[0]?.thumb_path
      || row?.images?.[0]?.image_path
      || row?.thumb_path
      || row?.image_path
      || row?.image_url
      || row?.image
      || '';
    const direct = getImageUrl(imgPath);
    if (direct) return direct;
    const uuid = row?.uuid;
    return (uuid && thumbMap[uuid]) || '';
  };

  const renderCell = (row, key) => {
    if (key === 'priority') {
      const rk = getRowKey(row);
      const current = row?.priority;
      const draftVal = (rk && priorityDrafts[rk] != null) ? priorityDrafts[rk] : (current == null ? '' : String(current));
      return (
        <input
          type="number"
          step="1"
          inputMode="numeric"
          aria-label="Priority"
          placeholder="—"
          value={draftVal}
          disabled={isRowSaving(row)}
          onChange={(e)=>{
            const v = e.target.value;
            // Allow empty, or integer-like input; do not commit until blur/Enter
            if (v === '' || /^-?\d*$/.test(v)) {
              setPriorityDrafts(prev => ({ ...prev, [rk]: v }));
            }
          }}
          onBlur={() => commitPriorityDraft(row)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitPriorityDraft(row);
            if (e.key === 'Escape') setPriorityDrafts(prev => { const n = { ...prev }; delete n[rk]; return n; });
          }}
          style={{ width: 90 }}
        />
      );
    }
    if (key === 'profile_type') {
      const code = row?.profile_type;
      const value = (code == null || code === '') ? '' : String(code);
      return (
        <select value={value} onChange={(e)=>inlineUpdate(row, 'profile_type', e.target.value)} disabled={isRowSaving(row)}>
          {PROFILE_TYPE_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      );
    }
    if (key === 'date_of_birth') {
      const raw = row?.date_of_birth;
      const baseVal = raw ? String(raw).slice(0, 10) : '';
      const rk = getRowKey(row);
      const draftVal = (rk && dateDrafts[rk] != null) ? dateDrafts[rk] : baseVal;
      return (
        <input
          type="text"
          inputMode="numeric"
          aria-label="Date of Birth"
          placeholder="YYYY-MM-DD"
          value={draftVal}
          disabled={isRowSaving(row)}
          onChange={(e)=>{
            const masked = maskDateInput(e.target.value);
            setDateDrafts(prev => ({ ...prev, [rk]: masked }));
          }}
          onBlur={() => commitDateDraft(row)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitDateDraft(row);
            if (e.key === 'Escape') setDateDrafts(prev => { const n = { ...prev }; delete n[rk]; return n; });
          }}
          style={{ width: 140 }}
        />
      );
    }
    if (key === 'gender') {
      const value = toGenderOption(row?.gender);
      return (
        <select value={value || ''} onChange={(e)=>inlineUpdate(row, 'gender', e.target.value)} disabled={isRowSaving(row)}>
          {GENDER_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      );
    }
    if (key === 'hobby') {
      const rk = getRowKey(row);
      const baseVal = (row?.hobby ?? '').toString();
      const draftVal = (rk && hobbyDrafts[rk] != null) ? hobbyDrafts[rk] : baseVal;
      return (
        <input
          type="text"
          aria-label="Hobby"
          placeholder="—"
          value={draftVal}
          disabled={isRowSaving(row)}
          onChange={(e)=>{
            setHobbyDrafts(prev => ({ ...prev, [rk]: e.target.value }));
          }}
          onBlur={() => commitHobbyDraft(row)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitHobbyDraft(row);
            if (e.key === 'Escape') setHobbyDrafts(prev => { const n = { ...prev }; delete n[rk]; return n; });
          }}
          style={{ width: 180 }}
        />
      );
    }
    if (key === 'is_public' || key === 'is_enabled' || key === 'is_enable') {
      const checked = !!row?.[key];
      return (
        <input type="checkbox" checked={checked} disabled={isRowSaving(row)} onChange={(e)=>inlineUpdate(row, key, e.target.checked)} />
      );
    }
    if (key === 'image') {
      const src = getRowImageUrl(row);
      if (!src) return '';
      return (
        <img
          src={src}
          alt={row?.name || row?.nickname || row?.uuid || 'profile'}
          onError={(e) => handleImageError(e)}
          loading="lazy"
          style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 4, display: 'block' }}
        />
      );
    }
    const val = row?.[key];
    if (key === 'created_at' || key === 'updated_at') {
      if (!val) return '';
      const d = new Date(val);
      return isNaN(d.getTime()) ? String(val) : d.toLocaleString();
    }
    if (typeof val === 'boolean') return val ? 'true' : 'false';
    return val ?? '';
  };

  // choose columns dynamically based on data keys
  const columns = useMemo(() => {
    const pref = ['image','id','uuid','email','nickname','name','hobby','gender','date_of_birth','profile_type','priority','is_public','is_enable','is_enabled','country','created_at','actions'];
    if (profiles.length === 0) return pref;
    const keys = new Set(pref);
    profiles.forEach(p => Object.keys(p || {}).forEach(k => keys.add(k)));
    return Array.from(keys).slice(0, pref.length); // keep exactly our preferred columns, no extras
  }, [profiles]);

  return (
    <>
    <div className="admin-section">
      <div className="admin-toolbar">
        <div className="field">
          <label>Channel</label>
          <select value={selectedChannel} onChange={onChannelChange}>
            <option value="">All channels</option>
            {channels.map((c) => {
              const id = c?.id || c?.key || c?.name || c;
              const label = c?.label || c?.name || c?.id || String(id);
              return <option key={String(id)} value={id}>{label}</option>;
            })}
          </select>
        </div>
        <div className="field">
          <label>Page size</label>
          <select value={limit} onChange={(e)=>{ setLimit(parseInt(e.target.value,10)); setOffset(0); }}>
            {[25,50,100].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div className="spacer" />
        <div className="pagination">
          <button onClick={prevPage} disabled={!canPrev || loading}>Prev</button>
          <span className="page-hint">offset {offset}{total!=null ? ` / ${total}` : ''}</span>
          <button onClick={nextPage} disabled={!canNext || loading}>Next</button>
        </div>
        <button onClick={fetchProfiles} disabled={loading} className="submit-button" style={{width:'auto'}}>Refresh</button>
      </div>

      {error && <div className="error-message" style={{marginBottom:12}}>{error}</div>}
      {loading ? (
        <div className="loading-row">Loading profiles…</div>
      ) : (
        <div className="table-wrap">
          <div style={{padding:'8px 10px', color:'#94a3b8'}}>Found {profiles.length}{total!=null?` of ~${total}`:''}</div>
          <table className="admin-table">
            <thead>
              <tr>
                {columns.map(col => <th key={col}>{col}</th>)}
              </tr>
            </thead>
            <tbody>
              {profiles.length === 0 ? (
                <tr><td colSpan={columns.length} style={{textAlign:'center', color:'#94a3b8'}}>No profiles</td></tr>
              ) : profiles.map((row, idx) => (
                <tr key={row.uuid || row.id || idx}>
                  {columns.map(col => (
                    <td key={col}>
                      {col === 'actions' ? (
                        <Link to={`/admin/profiles/${row.uuid || row.id}`} title="Edit" style={{padding:'6px 10px', display:'inline-block'}}>✏️</Link>
                      ) : (
                        renderCell(row, col)
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
    </>
  );
}
